import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/core/models/api-response.model';
import {
  CriarUsuarioPortalRequest,
  HierarquiaGrafoResposta,
  MoverHierarquiaRequest
} from 'src/app/pages/empresa/pages/organograma/models/hierarquia.model';

export interface GestorResumo {
  entidadeId: number;
  idOrigem: number;
  nome: string;
}

@Injectable({
  providedIn: 'root'
})
export class HierarquiaApiService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  obterGrafo(departamentoId: number, buscar?: string): Observable<HierarquiaGrafoResposta> {
    const params = this.construirParamsGrafo(departamentoId, buscar);
    return this.http
      .get<HierarquiaGrafoResposta | ApiResponse<HierarquiaGrafoResposta>>(`${this.baseUrl}/api/v1/hierarquia`, { params })
      .pipe(map((res) => this.unwrap(res)));
  }

  listarGestores(departamentoId: number): Observable<GestorResumo[]> {
    return this.http
      .get<GestorResumo[] | ApiResponse<GestorResumo[]>>(
        `${this.baseUrl}/api/v1/hierarquia/${departamentoId}/usuarios-vinculados`
      )
      .pipe(map((res) => this.unwrap(res)));
  }

  private construirParamsGrafo(departamentoId: number, buscar?: string): HttpParams {
    let params = new HttpParams().set('departamentoId', departamentoId);
    
    if (this.contemTextoBusca(buscar)) {
      params = params.set('buscar', buscar!.trim());
    }
    
    return params;
  }

  private contemTextoBusca(buscar?: string): boolean {
    return !!buscar?.trim();
  }

  alterarPerfilUsuarioPortal(
    usuarioId: number,
    perfil: 'GESTOR'
  ): Observable<{ sucesso: boolean }> {
    const body = { perfil: 'GESTOR' };
    return this.patch<{ sucesso: boolean }>(
      `${this.baseUrl}/api/v1/usuarios/${usuarioId}`,
      body
    );
  }

  atualizarCargoUsuarioPortal(
    usuarioId: number,
    cargo: string | null
  ): Observable<void> {
    const body = { cargo };
    return this.patch<void>(
      `${this.baseUrl}/api/v1/usuarios/${usuarioId}`,
      body
    );
  }

  /**
   * Orquestrador POST /api/v1/usuarios/{id}/atualizar-completo (ADR-006 — F4-16).
   *
   * Traduz o payload FLAT (interface historica do modal editar) para o formato
   * NESTED aceito pelo backend Node (`AtualizarCompletoInputDto`):
   *
   *   { dadosBasicos?, moverHierarquia?, timesGestor? }
   *
   * SEMANTICA "secao ausente = nao mexe":
   *   - `dadosBasicos`: so inclui se pelo menos 1 campo textual/enum estiver
   *     preenchido (`perfil | cargo | email | emailPessoal | telefone |
   *     jornadaHoras | dataContratacao | dataNascimento`). Evita PATCH que
   *     dispara `atualizado_em = NOW()` gratuito no backend.
   *   - `moverHierarquia`: so inclui se `payload.mover` presente. Mapeia
   *     `responsavelUsuarioId` -> `paiId` + `paiTipo: 'USUARIO'` (o modal atual
   *     so oferece responsavel GESTOR; se um dia entrar "mover para time",
   *     adiciona-se novo campo `responsavelTipo` no payload flat).
   *     `responsavelUsuarioId === null` = raiz (envia `paiId: null, paiTipo: null`).
   *   - `timesGestor`: sempre inclui quando `payload.timesIdsGestor !== undefined`
   *     (o modal SEMPRE monta a lista — mesmo vazia). Empacota em `{ timesIds }`.
   *     Se `payload.forcarTransferencia === true`, propaga a flag dentro de
   *     `timesGestor` (BUG-017 — usuario ja confirmou transferir times que
   *     ja pertencem a outros gestores).
   *
   * Historico: ate 2026-07-28 o service enviava o payload FLAT direto para o
   * Node. Como o DTO nested aplica `whitelist: true`, TODOS os campos flat eram
   * silenciosamente descartados — botao EDITAR nao alterava nada em prod.
   */
  atualizarUsuarioPortal(
    usuarioId: number,
    payload: {
      perfil?: 'GESTOR';
      cargo?: string | null;
      email?: string;
      emailPessoal?: string;
      telefone?: string;
      jornadaHoras?: number;
      dataContratacao?: string | null;
      dataNascimento?: string | null;
      timesIdsGestor?: number[];
      /**
       * BUG-017: quando `true`, propaga `forcarTransferencia: true` dentro
       * de `timesGestor` para o backend Node. So use apos confirmacao
       * explicita do admin no `TransferenciaTimesConfirmDialog`.
       */
      forcarTransferencia?: boolean;
      mover?: {
        departamentoId: number;
        /**
         * ID real do usuário responsável (idOrigem/usuarioId, não entidadeId).
         * `null` = mover para raiz.
         */
        responsavelUsuarioId: number | null;
        principal: boolean;
      };
    }
  ): Observable<void> {
    const body: {
      dadosBasicos?: Record<string, unknown>;
      moverHierarquia?: {
        paiId: number | null;
        paiTipo: 'USUARIO' | null;
        departamentoId: number;
        principal: boolean;
      };
      timesGestor?: { timesIds: number[]; forcarTransferencia?: boolean };
    } = {};

    const dadosBasicos = this.montarSecaoDadosBasicos(payload);
    if (dadosBasicos) {
      body.dadosBasicos = dadosBasicos;
    }

    if (payload.mover) {
      const responsavelId = payload.mover.responsavelUsuarioId;
      body.moverHierarquia = {
        paiId: responsavelId,
        // Modal atual so oferece GESTOR como responsavel. Raiz => paiTipo null.
        paiTipo: responsavelId === null ? null : 'USUARIO',
        departamentoId: payload.mover.departamentoId,
        principal: payload.mover.principal
      };
    }

    if (payload.timesIdsGestor !== undefined) {
      const timesGestor: { timesIds: number[]; forcarTransferencia?: boolean } = {
        timesIds: payload.timesIdsGestor
      };
      if (payload.forcarTransferencia === true) {
        timesGestor.forcarTransferencia = true;
      }
      body.timesGestor = timesGestor;
    }

    return this.post<void>(
      `${this.baseUrl}/api/v1/usuarios/${usuarioId}/atualizar-completo`,
      body
    );
  }

  /**
   * Extrai a secao `dadosBasicos` do payload flat. Retorna `undefined` quando
   * NENHUM campo textual/enum esta presente — nesse caso a secao inteira e
   * omitida do body (semantica "ausente = nao mexe" do ADR-006).
   */
  private montarSecaoDadosBasicos(payload: {
    perfil?: 'GESTOR';
    cargo?: string | null;
    email?: string;
    emailPessoal?: string;
    telefone?: string;
    jornadaHoras?: number;
    dataContratacao?: string | null;
    dataNascimento?: string | null;
  }): Record<string, unknown> | undefined {
    const secao: Record<string, unknown> = {};

    if (payload.perfil) {
      secao['perfil'] = payload.perfil;
    }
    if (payload.cargo !== undefined) {
      secao['cargo'] = payload.cargo;
    }
    if (payload.email !== undefined) {
      secao['email'] = payload.email;
    }
    if (payload.emailPessoal !== undefined) {
      secao['emailPessoal'] = payload.emailPessoal;
    }
    if (payload.telefone !== undefined) {
      secao['telefone'] = payload.telefone;
    }
    if (payload.jornadaHoras !== undefined) {
      secao['jornadaHoras'] = payload.jornadaHoras;
    }
    if (payload.dataContratacao !== undefined) {
      secao['dataContratacao'] = payload.dataContratacao;
    }
    if (payload.dataNascimento !== undefined) {
      secao['dataNascimento'] = payload.dataNascimento;
    }

    return Object.keys(secao).length > 0 ? secao : undefined;
  }

  criarUsuarioPortal(payload: CriarUsuarioPortalRequest): Observable<{ criados: number; falhas: number; erros: string[] }> {
    return this.post<{ criados: number; falhas: number; erros: string[] }>(`${this.baseUrl}/api/v1/usuarios`, [payload]);
  }

  criarUsuarioPortalLote(usuarios: CriarUsuarioPortalRequest[]): Observable<{ criados: number; falhas: number; erros: string[] }> {
    return this.post<{ criados: number; falhas: number; erros: string[] }>(`${this.baseUrl}/api/v1/usuarios`, usuarios);
  }

  excluirUsuarioPortal(usuarioId: number): Observable<void> {
    // v1: DELETE /api/v1/usuarios/{id}
    // Retorna 409 com mensagem "Nao e possivel excluir usuario com subordinados"
    // quando o usuário tem subordinados diretos (BFS 1-nível).
    return this.http.delete<void>(`${this.baseUrl}/api/v1/usuarios/${usuarioId}`);
  }

  /**
   * Move uma entidade na hierarquia usando o novo contrato do backend.
   * Envia paiId (usuarioId real) + paiTipo (USUARIO|TIME) em vez do legado novoResponsavelEntidadeId.
   */
  moverEntidadeHierarquia(
    entidadeId: number,
    request: MoverHierarquiaRequest
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/api/v1/hierarquia/entidades/${entidadeId}/mover`,
      request
    );
  }

  // Métodos auxiliares para reduzir duplicação
  private post<T>(url: string, body: any): Observable<T> {
    return this.http
      .post<T | ApiResponse<T>>(url, body)
      .pipe(map((res) => this.unwrap(res)));
  }

  private patch<T>(url: string, body: any): Observable<T> {
    return this.http
      .patch<T | ApiResponse<T>>(url, body)
      .pipe(map((res) => this.unwrap(res)));
  }

  private unwrap<T>(res: T | ApiResponse<T>): T {
    if ((res as ApiResponse<T>)?.conteudo !== undefined) {
      return (res as ApiResponse<T>).conteudo;
    }
    return res as T;
  }
}
